import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const PROPERTY_TYPES = ["Manufactured Housing", "Multifamily", "RV Resort", "Single-Family", "Self Storage", "HOA", "Mixed Portfolio"];
const PORTFOLIO_SIZES = ["1–250 units / pads", "251–500 units / pads", "501–1,500 units / pads", "1,500+ units / pads"];
const BOTTLENECKS = [
  "Onboarding & training staff",
  "Inspection consistency",
  "Compliance & fair housing",
  "Regional oversight",
  "All of the above",
];

const INITIAL = { full_name: "", email: "", phone: "", company: "", property_type: PROPERTY_TYPES[0], portfolio_size: PORTFOLIO_SIZES[0], bottleneck: BOTTLENECKS[0] };

const inputCls =
  "w-full rounded-md border border-neutral-300 bg-white px-4 py-3 text-sm text-[#11141A] placeholder:text-neutral-400 focus:outline-none focus:border-[#2E9CFF] focus:ring-2 focus:ring-[#2E9CFF]/20 transition-all";

const Field = ({ label, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="block text-[11px] font-mono font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-2">
      {label}
    </label>
    {children}
  </div>
);

export const DemoForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Demo request received", {
        description: "Our portfolio operations team will reach out within one business day.",
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error("Could not submit request", {
        description: formatApiError(err.response?.data?.detail),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="demo" className="relative py-24 sm:py-32 border-t border-slate-200" data-testid="demo-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5 lg:sticky lg:top-28"
        >
          <p className="eyebrow mb-4 flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-cadmium" /> Executive Demo
          </p>
          <h2 className="font-cabinet text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">
            See your portfolio run on <span className="font-editorial italic font-normal text-sky-600">one standard.</span>
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            A 30-minute working session with a portfolio operations specialist. We'll map your current onboarding,
            inspection, and audit workflows — and show you the exact OPERA360 configuration for your property types.
          </p>
          <ul className="mt-7 space-y-3 text-sm text-slate-900/85">
            <li className="flex gap-3"><span className="font-mono text-sky-600">01</span> Portfolio health walkthrough (your property types)</li>
            <li className="flex gap-3"><span className="font-mono text-sky-600">02</span> TRAIN onboarding track live build</li>
            <li className="flex gap-3"><span className="font-mono text-sky-600">03</span> AI Regional Manager query on sample data</li>
            <li className="flex gap-3"><span className="font-mono text-sky-600">04</span> Rollout plan &amp; per-property pricing</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          <form
            onSubmit={submit}
            data-testid="demo-form"
            className="rounded-md bg-bone text-[#11141A] p-7 sm:p-9 border border-neutral-300 shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full Name" htmlFor="demo-full-name">
                <input
                  id="demo-full-name"
                  data-testid="demo-form-full-name-input"
                  required
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Jordan Reyes"
                  className={inputCls}
                />
              </Field>
              <Field label="Work Email" htmlFor="demo-email">
                <input
                  id="demo-email"
                  data-testid="demo-form-email-input"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jordan@portfolio.com"
                  className={inputCls}
                />
              </Field>
              <Field label="Phone" htmlFor="demo-phone">
                <input
                  id="demo-phone"
                  data-testid="demo-form-phone-input"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(555) 214-8890"
                  className={inputCls}
                />
              </Field>
              <Field label="Company / Portfolio Name" htmlFor="demo-company">
                <input
                  id="demo-company"
                  data-testid="demo-form-company-input"
                  required
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Summit Community Group"
                  className={inputCls}
                />
              </Field>
              <Field label="Property Type" htmlFor="demo-ptype">
                <select
                  id="demo-ptype"
                  data-testid="demo-form-property-type-select"
                  value={form.property_type}
                  onChange={set("property_type")}
                  className={inputCls}
                >
                  {PROPERTY_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Portfolio Size" htmlFor="demo-psize">
                <select
                  id="demo-psize"
                  data-testid="demo-form-portfolio-size-select"
                  value={form.portfolio_size}
                  onChange={set("portfolio_size")}
                  className={inputCls}
                >
                  {PORTFOLIO_SIZES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Biggest Operational Bottleneck" htmlFor="demo-bottleneck">
                <select
                  id="demo-bottleneck"
                  data-testid="demo-form-bottleneck-select"
                  value={form.bottleneck}
                  onChange={set("bottleneck")}
                  className={inputCls}
                >
                  {BOTTLENECKS.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>
            </div>
            <button
              type="submit"
              data-testid="demo-form-submit-button"
              disabled={loading}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-white px-6 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-cadmium hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-60"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              {loading ? "Submitting…" : "Book My Executive Demo"}
            </button>
            <p className="mt-4 text-center text-[11px] font-mono uppercase tracking-wider text-neutral-400">
              No credit card · 30-minute session · portfolio pricing included
            </p>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
